import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

import PartnerCatalog from '../components/partner/PartnerCatalog';
import PartnerCart from '../components/partner/PartnerCart';
import PartnerOrders from '../components/partner/PartnerOrders';
import PartnerProfile from '../components/partner/PartnerProfile';
import PartnerQuotes from '../components/partner/PartnerQuotes';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PartnerDashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [orders, setOrders] = useState([]);
  const [activeTab, setActiveTab] = useState('catalog');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('All');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [orderNotes, setOrderNotes] = useState('');

  const isPartner = user?.role === 'partner';
  const brandDiscounts = user?.brand_discounts || {};
  const approvedBrands = user?.approved_brands || [];

  useEffect(() => {
    if (isPartner) {
      fetchProducts();
      fetchOrders();
    }
  }, [isPartner]);

  const fetchProducts = async () => {
    try {
      const { data } = await axios.get(`${API}/products/list`, { withCredentials: true });
      setProducts(data);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    try {
      const { data } = await axios.get(`${API}/partner/orders`, { withCredentials: true });
      setOrders(data);
    } catch (error) {
      toast.error('Failed to load orders');
    }
  };

  // VAT rate applied throughout the Partner Portal (South Africa: 15%)
  const VAT_RATE = 0.15;

  // Calculate discounted price based on brand — returns BOTH excl & incl VAT
  const getDiscountedPrice = (product) => {
    const baseExcl = Number(product.base_price_excl ?? product.base_price ?? 0);
    const baseIncl = Number(product.base_price_incl ?? baseExcl * (1 + VAT_RATE));
    const discount = brandDiscounts[product.brand] || 0;
    const partnerExcl = baseExcl * (1 - discount / 100);
    const partnerIncl = baseIncl * (1 - discount / 100);
    return { baseExcl, baseIncl, partnerExcl, partnerIncl, discount };
  };

  // Filter products by approved brands and search/filter
  const filteredProducts = products.filter((product) => {
    if (!approvedBrands.includes(product.brand)) return false;
    if (selectedBrand !== 'All' && product.brand !== selectedBrand) return false;
    if (selectedCategory !== 'All' && product.category !== selectedCategory) return false;

    const searchLower = searchTerm.toLowerCase();
    if (
      searchTerm &&
      !(
        product.sku?.toLowerCase().includes(searchLower) ||
        product.product_name?.toLowerCase().includes(searchLower) ||
        product.description?.toLowerCase().includes(searchLower)
      )
    )
      return false;

    return true;
  });

  const addToCart = (product) => {
    const { partnerExcl, partnerIncl, discount } = getDiscountedPrice(product);
    const existing = cart.find((item) => item.sku === product.sku);
    if (existing) {
      setCart(
        cart.map((item) =>
          item.sku === product.sku ? { ...item, quantity: item.quantity + 1 } : item
        )
      );
    } else {
      setCart([
        ...cart,
        {
          ...product,
          quantity: 1,
          // Persist the partner prices at the moment of add so subsequent
          // discount-rate changes mid-session don't retroactively affect the cart.
          partner_price_excl: partnerExcl,
          partner_price_incl: partnerIncl,
          applied_discount: discount,
        },
      ]);
    }
    toast.success(`${product.product_name} added to cart`);
  };

  const updateQuantity = (sku, quantity) => {
    if (quantity <= 0) {
      removeFromCart(sku);
      return;
    }
    setCart(cart.map((item) => (item.sku === sku ? { ...item, quantity } : item)));
  };

  const removeFromCart = (sku) => {
    setCart(cart.filter((item) => item.sku !== sku));
  };

  const submitOrder = async () => {
    if (cart.length === 0) {
      toast.error('Cart is empty');
      return;
    }

    const items = cart.map((item) => {
      const unitExcl = Number(item.partner_price_excl ?? item.base_price_excl ?? item.base_price ?? 0);
      const unitIncl = Number(
        item.partner_price_incl ?? item.base_price_incl ?? unitExcl * (1 + VAT_RATE)
      );
      return {
        sku: item.sku,
        product_name: item.product_name,
        quantity: item.quantity,
        // New canonical fields — both VAT variants
        unit_price_excl: unitExcl,
        unit_price_incl: unitIncl,
        // Backwards-compat (EXCL — same value that was stored pre-fix)
        unit_price: unitExcl,
      };
    });

    try {
      await axios.post(
        `${API}/partner/orders`,
        { items, notes: orderNotes },
        { withCredentials: true }
      );
      toast.success('Order submitted successfully! We will contact you with a quote.');
      setCart([]);
      setOrderNotes('');
      fetchOrders();
      setActiveTab('orders');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit order');
    }
  };

  const cartTotals = cart.reduce(
    (acc, item) => {
      const excl = Number(item.partner_price_excl ?? item.base_price_excl ?? item.base_price ?? 0);
      const incl = Number(
        item.partner_price_incl ?? item.base_price_incl ?? excl * (1 + VAT_RATE)
      );
      acc.excl += excl * item.quantity;
      acc.incl += incl * item.quantity;
      return acc;
    },
    { excl: 0, incl: 0 }
  );
  const cartVat = cartTotals.incl - cartTotals.excl;

  if (authLoading) {
    return (
      <div className="min-h-screen pt-20 bg-ewss-navy flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (!isPartner) {
    return <Navigate to="/partner-portal" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen pt-20 bg-ewss-navy flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  const tabs = [
    { id: 'catalog', label: 'Product Catalog' },
    { id: 'cart', label: `Cart (${cart.length})` },
    { id: 'quotes', label: 'My Quotes' },
    { id: 'orders', label: `Order History (${orders.length})` },
    { id: 'profile', label: 'Profile Settings' },
  ];

  return (
    <div className="min-h-screen pt-20 bg-ewss-navy">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Partner Dashboard</h1>
          <p className="text-gray-400">Welcome back, {user?.company}</p>
          {user?.discount_percentage > 0 && (
            <p className="text-ewss-cyan font-semibold">
              Your discount: {user.discount_percentage}%
            </p>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-gray-700" data-testid="partner-dashboard-tabs">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`px-6 py-3 font-semibold transition-colors ${
                activeTab === t.id
                  ? 'text-ewss-cyan border-b-2 border-ewss-cyan'
                  : 'text-gray-400 hover:text-white'
              }`}
              data-testid={`partner-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {activeTab === 'catalog' && (
          <PartnerCatalog
            approvedBrands={approvedBrands}
            brandDiscounts={brandDiscounts}
            filteredProducts={filteredProducts}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            selectedBrand={selectedBrand}
            setSelectedBrand={setSelectedBrand}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            getDiscountedPrice={getDiscountedPrice}
            addToCart={addToCart}
          />
        )}

        {activeTab === 'cart' && (
          <PartnerCart
            cart={cart}
            updateQuantity={updateQuantity}
            removeFromCart={removeFromCart}
            orderNotes={orderNotes}
            setOrderNotes={setOrderNotes}
            cartTotalExcl={cartTotals.excl}
            cartTotalIncl={cartTotals.incl}
            cartVat={cartVat}
            vatRate={VAT_RATE}
            submitOrder={submitOrder}
          />
        )}

        {activeTab === 'orders' && <PartnerOrders orders={orders} />}

        {activeTab === 'quotes' && (
          <PartnerQuotes
            products={products.filter((p) => approvedBrands.includes(p.brand))}
            brandDiscounts={brandDiscounts}
            refreshOrders={fetchOrders}
          />
        )}

        {activeTab === 'profile' && <PartnerProfile user={user} />}
      </div>
    </div>
  );
};

export default PartnerDashboard;
